import React, { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import RealTime from './RealTime'
import useDetectDevice from '@/v2/hooks/useDetectDevice'

const counters = data => ({
  messages: data?.chat?.messagesUnreadCount ?? 0,
  conversations: data?.chat?.conversationsUnreadCount ?? 0,
  tasks: data?.tasks?.uncompletedCount ?? 0,
})

// Every counter broadcast carries the current totals, including the ones sent
// when something is read or a task is saved, so only a count going up means
// something new arrived. Marking a conversation as unread counts too: the
// office uses it to flag a conversation for everyone, so it sounds on every
// computer.
export const hasNewActivity = (previous, next) => {
  const before = counters(previous)
  const after = counters(next)

  return Object.keys(after).some(key => after[key] > before[key])
}

const RTNotifications = () => {
  const queryClient = useQueryClient()
  const { isMobile } = useDetectDevice()
  const audioPlayerRef = useRef(null)

  // Browsers block audio until the user has interacted with the page, and
  // Safari wants this very element to have played once inside a gesture. The
  // first click or keypress after a load plays it muted to lift that block.
  useEffect(() => {
    const events = ['pointerdown', 'keydown']

    const unlock = () => {
      const audio = audioPlayerRef.current
      if (!audio) return

      audio.muted = true
      audio
        .play()
        .then(() => {
          audio.pause()
          audio.currentTime = 0
          events.forEach(event => document.removeEventListener(event, unlock))
        })
        .catch(() => {})
        .finally(() => {
          audio.muted = false
        })
    }

    events.forEach(event => document.addEventListener(event, unlock))

    return () =>
      events.forEach(event => document.removeEventListener(event, unlock))
  }, [])

  const handleSendPush = () => {
    if (!('Notification' in window)) {
      console.log('This browser does not support desktop notification')
      return
    }

    Notification.requestPermission().then(permission => {
      if (permission === 'granted') {
        const notification = new Notification('Clearworks', {
          body: 'A new message has arrived!',
          icon: '/favicons/apple-icon-180x180.png',
          badge: '/favicons/apple-icon-180x180.png',
          requireInteraction: true,
          renotify: true,
          tag: 'new-message',
        })

        notification.onclick = () => {
          notification.close()
          window.parent.focus()
        }
      }
    })
  }

  const playSound = () => {
    const audio = audioPlayerRef.current
    if (!audio) return

    // Restart it so a second message right after the first still makes a
    // sound instead of being swallowed by the one already playing.
    audio.currentTime = 0
    audio.play().catch(e => console.error('Notification sound blocked:', e))
  }

  const handleEvent = ({ data }) => {
    const queryKey = ['Notification']
    const previousData = queryClient.getQueryData(queryKey)

    // Chat and tasks broadcast their counters separately, so merge them in
    // rather than letting one wipe out the other.
    queryClient.setQueryData(queryKey, previous => ({ ...previous, ...data }))

    if (hasNewActivity(previousData, data)) {
      playSound()
      handleSendPush()
    }
  }

  return (
    <>
      <audio ref={audioPlayerRef} src="/sounds/notification.mp3" />
      <RealTime
        channel="NotificationsChannel"
        onEvent={handleEvent}
        keepAliveInBackground={!isMobile}
      />
    </>
  )
}

export default RTNotifications
